import React from 'react';
import { Popover } from 'react-bootstrap';
import { OverlayInjectedProps, OverlayTriggerProps } from 'react-bootstrap/OverlayTrigger';
import { Link } from 'react-router-dom';
import { BSOverlay } from '~/components/Bootstrap';
import { LitePlanIndicator, RiskBandElevation, SrfIndicator } from '~/components/Patient';
import { InProgressAlert, LabeledColumnField, PopoverContent } from '~/components/SearchResults';
import { formatDate } from '~/util';

// Type assertion for TypeScript 5.x compatibility with react-router-dom v5
const LinkComponent = Link as any;

interface ResultsRowProps {
    patient: PatientType;
    labeledColumnFields: LabeledColumnField[];
}
function ResultsRow({ patient, labeledColumnFields }: ResultsRowProps) {
    const renderTooltip = (props: OverlayInjectedProps) => {
        return (
            <Popover {...props}>
                <Popover.Body>
                    <PopoverContent patient={patient} />
                </Popover.Body>
            </Popover>
        );
    };
    const overlayProps = {
        placement: 'top-start',
        delay: { show: 1500, hide: 0 },
        overlay: renderTooltip,
    } as OverlayTriggerProps;

    function getDemos(name: string, value: string) {
        return (
            <BSOverlay {...overlayProps}>
                <td data-name={name}>
                    <LinkComponent to={`/patients/${patient.id}`}>{value}</LinkComponent>
                    {name === 'dob' && (
                        <div className="pull-right">
                            <LitePlanIndicator patient={patient} />
                            <SrfIndicator patient={patient} />
                        </div>
                    )}
                </td>
            </BSOverlay>
        );
    }

    function getCellValue(x: LabeledColumnField) {
        const properties = x.field.split('.');
        const nestedProp = properties[properties.length - 1];
        const cellValue = properties.reduce((prev: any, curr) => prev?.[curr], patient);
        const emptyValue = '--';

        if (nestedProp === 'oldest_answer_at') {
            return cellValue ? (
                <>
                    {formatDate(cellValue)}
                    <InProgressAlert timestamp={cellValue} />
                </>
            ) : (
                emptyValue
            );
        }
        if (nestedProp === 'last_office_visit') {
            return cellValue ? formatDate(cellValue) : emptyValue;
        }
        // don't default to 0 if risk_band_level is null
        if (nestedProp === 'risk_band_level') {
            return cellValue === null ? (
                emptyValue
            ) : (
                <>
                    {cellValue}
                    <RiskBandElevation metadata={patient.metadata} />
                </>
            );
        }
        return cellValue || 0;
    }

    return (
        <tr>
            {getDemos('fname', patient.first_name)}
            {getDemos('lname', patient.last_name)}
            {getDemos('dob', formatDate(patient.dob))}
            {labeledColumnFields.map((x) => (
                <BSOverlay key={x.label} {...overlayProps}>
                    <td data-name={x.label} className="d-none d-lg-table-cell">
                        {getCellValue(x)}
                    </td>
                </BSOverlay>
            ))}
        </tr>
    );
}

export { ResultsRow };
